import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Power,
  PowerOff,
  Thermometer,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { subscribeUserDevices } from "../services/deviceService";
import { subscribeDeviceHistory } from "../services/historyService";
import { inferDeviceType, deviceTypeLabel } from "../utils/deviceCapabilities";
import { normalizeTimestampMs, pumpRunNote, formatCountdown, pumpRunRemainingSec, formatSensorValue, sanitizeSensorSnapshot } from "../utils/pumpActivityUtils";

function formatDuration(sec) {
  if (sec === null || sec === undefined || !Number.isFinite(Number(sec))) return "—";
  const s = Math.max(0, Math.round(Number(sec)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m}m ${r}s`;
  if (m) return `${m}m ${r}s`;
  return `${r}s`;
}

const PAGE_SIZE = 12;

function ThemedSelect({ value, onChange, disabled, children, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="h-10 w-full appearance-none rounded-xl border border-input bg-cream/50 pl-3 pr-9 text-sm text-foreground outline-none transition-colors focus:ring-2 focus:ring-ring disabled:opacity-50"
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

function ThemedDate({ value, onChange, className = "" }) {
  return (
    <input
      type="date"
      value={value}
      onChange={onChange}
      className={`h-10 w-full min-w-0 rounded-xl border border-input bg-cream/50 px-3 text-sm text-foreground outline-none transition-colors focus:ring-2 focus:ring-ring [color-scheme:light] ${className}`}
    />
  );
}

function pageSlice(rows, page) {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const start = safePage * PAGE_SIZE;
  return {
    items: rows.slice(start, start + PAGE_SIZE),
    total,
    totalPages,
    page: safePage,
    from: total ? start + 1 : 0,
    to: Math.min(start + PAGE_SIZE, total),
  };
}

function HistoryPager({ pageInfo, onPage, unitLabel }) {
  if (!pageInfo.total) return null;
  return (
    <div className="flex items-center justify-between gap-2 border-t border-border/70 px-3 py-2.5 sm:px-5 sm:py-3 bg-cream/30">
      <div className="text-[11px] sm:text-xs text-muted-foreground min-w-0 truncate">
        {pageInfo.from}–{pageInfo.to} of {pageInfo.total}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2.5"
          disabled={pageInfo.page <= 0}
          onClick={() => onPage(pageInfo.page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Previous</span>
        </Button>
        <span className="text-[11px] sm:text-xs text-muted-foreground min-w-[3.5rem] text-center tabular-nums">
          {pageInfo.page + 1}/{pageInfo.totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2.5"
          disabled={pageInfo.page >= pageInfo.totalPages - 1}
          onClick={() => onPage(pageInfo.page + 1)}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function statusLabel(status) {
  switch (status) {
    case "queued":
      return "Queued";
    case "running":
      return "Running";
    case "completed":
      return "Completed (auto off)";
    case "stopped":
      return "Stopped";
    case "cancelled":
      return "Cancelled";
    case "failed":
      return "Failed";
    default:
      return status || "—";
  }
}

function stopReasonLabel(reason) {
  const map = {
    auto_off: "Auto off (timer)",
    user_off: "User OFF (manual)",
    user_off_pending: "User OFF pending",
    user_off_before_start: "Cancelled before start",
    superseded_by_new_on: "Replaced by new ON",
    superseded_by_manual_off: "Manual OFF",
    ack_timeout: "Device timeout",
    ack_failed: "Command failed",
  };
  if (!reason) return "—";
  return map[reason] || String(reason).replace(/_/g, " ");
}

function triggerBadgeClass(kind) {
  switch (kind) {
    case "user":
      return "bg-accent/15 text-accent border-accent/30";
    case "smart_rule":
      return "bg-primary/10 text-primary border-primary/25";
    case "smart_irrigation":
      return "bg-success/15 text-success border-success/30";
    case "schedule":
      return "bg-muted text-foreground border-border";
    default:
      return "bg-cream text-muted-foreground border-border";
  }
}

function EventPill({ kind, label }) {
  const isOn = kind === "motor_on";
  const isOff = kind === "motor_off";
  const isSensor = kind === "sensor";
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        isOn && "bg-success/15 text-success border-success/30",
        isOff && "bg-destructive/10 text-destructive border-destructive/25",
        isSensor && "bg-cream text-foreground border-border",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {isOn ? <Power className="h-3 w-3" /> : null}
      {isOff ? <PowerOff className="h-3 w-3" /> : null}
      {isSensor ? <Thermometer className="h-3 w-3" /> : null}
      {label}
    </span>
  );
}

export default function HistoryPage() {
  const [userDevices, setUserDevices] = useState({});
  const [deviceHistory, setDeviceHistory] = useState({});
  const [pumpRunsByDevice, setPumpRunsByDevice] = useState({});
  const [pumpWarningsByDevice, setPumpWarningsByDevice] = useState({});
  const [pumpLogsByDevice, setPumpLogsByDevice] = useState({});
  const [selectedDevice, setSelectedDevice] = useState("");
  const [selectedNode, setSelectedNode] = useState("all");
  const [sensorFilter, setSensorFilter] = useState("all");
  const [triggerFilter, setTriggerFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [view, setView] = useState("activity");
  const [activityPage, setActivityPage] = useState(0);
  const [sensorPage, setSensorPage] = useState(0);
  const [pumpRunPage, setPumpRunPage] = useState(0);
  const [warningPage, setWarningPage] = useState(0);
  const [logPage, setLogPage] = useState(0);
  const { user } = useAuth();
  const autoSelectedRef = useRef(false);
  const historyUnsubsRef = useRef([]);

  const selectableDevices = useMemo(() => {
    return Object.entries(userDevices)
      .filter(([id, d]) => inferDeviceType(id, d?.deviceType) !== "node")
      .sort((a, b) => String(a[1]?.name || a[0]).localeCompare(String(b[1]?.name || b[0])));
  }, [userDevices]);

  const selectedDeviceType = useMemo(
    () => (selectedDevice ? inferDeviceType(selectedDevice, userDevices[selectedDevice]?.deviceType) : ""),
    [selectedDevice, userDevices]
  );

  const linkedNodes = useMemo(() => {
    if (selectedDeviceType !== "hub") return [];
    const list = userDevices[selectedDevice]?.linkedNodes;
    return (Array.isArray(list) ? list : [])
      .slice()
      .sort((a, b) => (a.potIndex || 0) - (b.potIndex || 0));
  }, [selectedDevice, selectedDeviceType, userDevices]);

  const isHub = selectedDeviceType === "hub";

  useEffect(() => {
    if (!user) return;

    const unsubUserDevices = subscribeUserDevices(user.uid, (devices) => {
      setUserDevices(devices);

      const allIds = Object.keys(devices);
      const pickable = allIds.filter((id) => inferDeviceType(id, devices[id]?.deviceType) !== "node");

      if (pickable.length > 0 && !autoSelectedRef.current) {
        autoSelectedRef.current = true;
        const simId = pickable.find((id) => devices[id]?.isSimulator || id.includes("_sim_"));
        setSelectedDevice(simId || pickable[0]);
      }

      // Resubscribe history for hubs, standalones, and owned pot nodes
      historyUnsubsRef.current.forEach((fn) => fn());
      historyUnsubsRef.current = [];
      allIds.forEach((id) => {
        const unsubHistory = subscribeDeviceHistory(id, (historyData) => {
          const sensors = historyData?.sensors || (historyData && !historyData.pumpRuns ? historyData : {});
          setDeviceHistory((prev) => ({
            ...prev,
            [id]: sensors,
          }));
          setPumpRunsByDevice((prev) => ({
            ...prev,
            [id]: historyData?.pumpRuns || [],
          }));
          setPumpWarningsByDevice((prev) => ({
            ...prev,
            [id]: historyData?.pumpWarnings || [],
          }));
          setPumpLogsByDevice((prev) => ({
            ...prev,
            [id]: historyData?.pumpLogs || [],
          }));
        });
        historyUnsubsRef.current.push(unsubHistory);
      });
    });

    return () => {
      unsubUserDevices();
      historyUnsubsRef.current.forEach((fn) => fn());
      historyUnsubsRef.current = [];
    };
  }, [user]);

  useEffect(() => {
    setSelectedNode("all");
    if (selectedDeviceType === "hub") {
      setView((v) => (v === "activity" ? "sensors" : v));
    }
  }, [selectedDevice, selectedDeviceType]);

  useEffect(() => {
    if (selectedNode === "all") return;
    if (!linkedNodes.some((n) => n.nodeId === selectedNode)) {
      setSelectedNode("all");
    }
  }, [linkedNodes, selectedNode]);

  const getFormattedDate = (savedAt) => {
    const ms = normalizeTimestampMs(savedAt);
    if (!ms) return "—";
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(d);
  };

  const getFormattedTime = (savedAt) => {
    const ms = normalizeTimestampMs(savedAt);
    if (!ms) return "—";
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    }).format(d);
  };

  const toDateObject = (savedAt) => {
    const ms = normalizeTimestampMs(savedAt);
    if (!ms) return null;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const inDateRange = useCallback((ms) => {
    const rowDate = toDateObject(ms);
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const to = toDate ? new Date(`${toDate}T23:59:59`) : null;
    if ((from || to) && !rowDate) return false;
    if (from && rowDate < from) return false;
    if (to && rowDate > to) return false;
    return true;
  }, [fromDate, toDate]);

  const historyRows = useMemo(() => {
    if (!selectedDevice) return [];

    // Hub has no sensor snapshots — show linked pot node history instead
    if (isHub) {
      const nodes =
        selectedNode === "all"
          ? linkedNodes
          : linkedNodes.filter((n) => n.nodeId === selectedNode);
      const rows = [];
      nodes.forEach((n) => {
        const nodeId = n.nodeId;
        if (!nodeId) return;
        const history = deviceHistory[nodeId];
        const entries = Array.isArray(history) ? history : Object.entries(history || {});
        entries.forEach(([timestamp, data]) => {
          rows.push({
            timestamp: `${nodeId}:${timestamp}`,
            nodeId,
            nodeName: n.name || nodeId,
            potIndex: n.potIndex || null,
            data: data || {},
          });
        });
      });
      return rows.sort((a, b) => (Number(b.data?.savedAt) || 0) - (Number(a.data?.savedAt) || 0));
    }

    const history = deviceHistory[selectedDevice];
    const entries = Array.isArray(history) ? history : Object.entries(history || {});
    const arr = entries.map(([timestamp, data]) => ({
      timestamp,
      nodeId: null,
      nodeName: null,
      potIndex: null,
      data: data || {},
    }));
    return arr.sort((a, b) => (Number(b.data?.savedAt) || 0) - (Number(a.data?.savedAt) || 0));
  }, [deviceHistory, isHub, linkedNodes, selectedDevice, selectedNode]);

  const filteredRows = useMemo(() => {
    return historyRows.filter((row) => {
      if (!inDateRange(row.data?.savedAt)) return false;
      if (sensorFilter === "all") return true;
      if (sensorFilter === "temperature") return row.data?.Temperature !== undefined && row.data?.Temperature !== null;
      if (sensorFilter === "humidity") return row.data?.Humidity !== undefined && row.data?.Humidity !== null;
      if (sensorFilter === "soil") return row.data?.SoilMoisture !== undefined && row.data?.SoilMoisture !== null;
      return true;
    });
  }, [historyRows, inDateRange, sensorFilter]);

  const pumpRuns = useMemo(() => {
    // Motor history lives on the hub / standalone device (not pot nodes)
    if (isHub && selectedNode !== "all") return [];
    const rows = pumpRunsByDevice[selectedDevice] || [];
    return rows.filter((r) => {
      if (!inDateRange(r.startedAt || r.queuedAt)) return false;
      if (triggerFilter === "all") return true;
      return (r.triggerKind || "other") === triggerFilter;
    });
  }, [inDateRange, isHub, pumpRunsByDevice, selectedDevice, selectedNode, triggerFilter]);

  const pumpWarnings = useMemo(() => {
    if (isHub && selectedNode !== "all") return [];
    const rows = pumpWarningsByDevice[selectedDevice] || [];
    return rows.filter((r) => inDateRange(r.at));
  }, [inDateRange, isHub, pumpWarningsByDevice, selectedDevice, selectedNode]);

  const pumpLogs = useMemo(() => {
    if (isHub && selectedNode !== "all") return [];
    const rows = pumpLogsByDevice[selectedDevice] || [];
    return rows.filter((r) => inDateRange(r.at));
  }, [inDateRange, isHub, pumpLogsByDevice, selectedDevice, selectedNode]);

  const activityRows = useMemo(() => {
    const rows = [];

    // Hub motor commands later; still show if any exist when viewing hub (all nodes)
    if (!isHub || selectedNode === "all") {
      pumpRuns.forEach((r) => {
        const onAt = normalizeTimestampMs(r.startedAt || r.queuedAt);
        if (onAt) {
          rows.push({
            id: `${r.id}-on`,
            at: onAt,
            kind: "motor_on",
            eventLabel: "Motor ON",
            trigger: r.sourceLabel || r.source || "—",
            triggerKind: r.triggerKind || "other",
            duration: r.plannedDurationSec,
            status: statusLabel(r.status),
            nodeLabel: isHub ? "Hub pump" : null,
            detail: (() => {
              const snap = sanitizeSensorSnapshot(r);
              return `Planned ${formatDuration(r.plannedDurationSec)} · ${formatSensorValue(snap.tempAtStart)}°C · ${formatSensorValue(snap.humidityAtStart)}% · Soil ${formatSensorValue(snap.soilAtStart)}%`;
            })(),
          });
        }
        const offAt = normalizeTimestampMs(r.endedAt);
        if (offAt) {
          rows.push({
            id: `${r.id}-off`,
            at: offAt,
            kind: "motor_off",
            eventLabel: "Motor OFF",
            trigger: stopReasonLabel(r.stopReason),
            triggerKind: r.triggerKind || "other",
            duration: r.actualDurationSec,
            status: statusLabel(r.status),
            nodeLabel: isHub ? "Hub pump" : null,
            detail: pumpRunNote(r),
          });
        }
      });
    }

    // Sensor snapshots: standalone always; hub from pot nodes only
    if (view === "activity" && sensorFilter !== "motor" && (!isHub || linkedNodes.length > 0)) {
      filteredRows.forEach((row) => {
        rows.push({
          id: `sensor-${row.timestamp}`,
          at: row.data?.savedAt || 0,
          kind: "sensor",
          eventLabel: "Sensor snapshot",
          trigger: "Hourly log",
          triggerKind: "other",
          duration: null,
          status: "—",
          nodeLabel: row.nodeName || row.nodeId || null,
          detail: `Temp ${row.data?.Temperature ?? "—"}°C · Humidity ${row.data?.Humidity ?? "—"}% · Soil ${row.data?.SoilMoisture ?? "—"}%`,
        });
      });
    }

    return rows.sort((a, b) => (b.at || 0) - (a.at || 0));
  }, [filteredRows, isHub, linkedNodes.length, pumpRuns, selectedNode, sensorFilter, view]);

  useEffect(() => {
    setActivityPage(0);
    setSensorPage(0);
    setPumpRunPage(0);
    setWarningPage(0);
    setLogPage(0);
  }, [selectedDevice, selectedNode, sensorFilter, triggerFilter, fromDate, toDate, view]);

  const activityPaged = useMemo(() => pageSlice(activityRows, activityPage), [activityRows, activityPage]);
  const sensorPaged = useMemo(() => pageSlice(filteredRows, sensorPage), [filteredRows, sensorPage]);
  const pumpRunPaged = useMemo(() => pageSlice(pumpRuns, pumpRunPage), [pumpRuns, pumpRunPage]);
  const warningPaged = useMemo(() => pageSlice(pumpWarnings, warningPage), [pumpWarnings, warningPage]);
  const logPaged = useMemo(() => pageSlice(pumpLogs, logPage), [pumpLogs, logPage]);

  const trend = useMemo(() => {
    const a = historyRows[0]?.data;
    const b = historyRows[1]?.data;
    if (!a || !b) return null;
    function dir(key) {
      const av = a?.[key];
      const bv = b?.[key];
      if (av === undefined || bv === undefined) return "flat";
      if (av > bv) return "up";
      if (av < bv) return "down";
      return "flat";
    }
    return { t: dir("Temperature"), h: dir("Humidity"), s: dir("SoilMoisture") };
  }, [historyRows]);

  const motorOnCount = useMemo(
    () => activityRows.filter((r) => r.kind === "motor_on").length,
    [activityRows]
  );

  const exportCsv = () => {
    if (!selectedDevice) return;
    let lines;
    let name;
    if (view === "activity") {
      name = `activity_${selectedDevice}${isHub && selectedNode !== "all" ? `_${selectedNode}` : ""}.csv`;
      lines = isHub
        ? ["when,event,node,trigger,durationSec,status,detail"]
        : ["when,event,trigger,durationSec,status,detail"];
      activityRows.forEach((r) => {
        const base = [
          r.at ? new Date(r.at).toISOString() : "",
          r.eventLabel,
        ];
        if (isHub) base.push(`"${String(r.nodeLabel || "").replace(/"/g, '""')}"`);
        base.push(
          `"${String(r.trigger || "").replace(/"/g, '""')}"`,
          r.duration ?? "",
          r.status || "",
          `"${String(r.detail || "").replace(/"/g, '""')}"`
        );
        lines.push(base.join(","));
      });
    } else if (view === "pump") {
      name = `pump_history_${selectedDevice}.csv`;
      lines = [
        "started,stopped,ranSec,plannedSec,trigger,status,stopReason,warning,soilAtStart,tempAtStart,humidityAtStart",
      ];
      pumpRuns.forEach((r) => {
        lines.push(
          [
            r.startedAt || r.queuedAt ? new Date(r.startedAt || r.queuedAt).toISOString() : "",
            r.endedAt ? new Date(r.endedAt).toISOString() : "",
            r.actualDurationSec ?? "",
            r.plannedDurationSec ?? "",
            r.sourceLabel || r.source || "",
            r.status || "",
            r.stopReason || "",
            r.warning || "",
            r.soilAtStart ?? "",
            r.tempAtStart ?? "",
            r.humidityAtStart ?? "",
          ].join(",")
        );
      });
    } else {
      name = `history_${selectedDevice}${isHub && selectedNode !== "all" ? `_${selectedNode}` : ""}.csv`;
      lines = isHub
        ? ["timestamp,nodeId,nodeName,date,time,Temperature,Humidity,SoilMoisture"]
        : ["timestamp,date,time,Temperature,Humidity,SoilMoisture"];
      filteredRows.forEach((r) => {
        const d = r.data || {};
        if (isHub) {
          lines.push(
            [
              r.timestamp,
              r.nodeId || "",
              r.nodeName || "",
              getFormattedDate(d.savedAt),
              getFormattedTime(d.savedAt),
              d?.Temperature ?? "",
              d?.Humidity ?? "",
              d?.SoilMoisture ?? "",
            ].join(",")
          );
        } else {
          lines.push(
            [
              r.timestamp,
              getFormattedDate(d.savedAt),
              getFormattedTime(d.savedAt),
              d?.Temperature ?? "",
              d?.Humidity ?? "",
              d?.SoilMoisture ?? "",
            ].join(",")
          );
        }
      });
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const resetFilters = () => {
    setSensorFilter("all");
    setTriggerFilter("all");
    setFromDate("");
    setToDate("");
    if (isHub) setSelectedNode("all");
  };

  const viewTabs = useMemo(() => {
    if (isHub) {
      return [
        { id: "sensors", label: "Pot sensors", short: "Sensors" },
        { id: "activity", label: "Activity log", short: "Activity" },
        { id: "pump", label: "Motor (hub)", short: "Motor" },
      ];
    }
    return [
      { id: "activity", label: "Activity log", short: "Activity" },
      { id: "sensors", label: "Sensor snapshots", short: "Sensors" },
      { id: "pump", label: "Motor runs & warnings", short: "Motor" },
    ];
  }, [isHub]);

  const sensorEmptyMessage = isHub
    ? linkedNodes.length === 0
      ? "No pot nodes linked to this hub yet. Link a node on Devices, then hourly soil / temp / humidity will appear here."
      : selectedNode !== "all"
        ? "No hourly history yet for this pot node."
        : "No hourly pot history yet for this hub’s nodes."
    : "No hourly history yet for this device.";

  const activityEmptyMessage = isHub
    ? "No pot sensor snapshots yet. Hub motor ON/OFF will appear here later when the pump is connected."
    : "No activity yet. Sensor snapshots and motor ON/OFF (user, schedule, or smart rule) will appear here.";

  const pumpEmptyMessage = isHub
    ? "Hub motor history will appear here once a pump is connected and used. Pot nodes use valves (history later)."
    : "No motor runs yet. After the next ON (user, schedule, or smart rule), it will appear here with start time, duration, and trigger.";

  return (
    <PageShell>
      <PageHero
        eyebrow="History"
        title="History."
        highlight="Trends & motor logs."
        description={
          isHub
            ? "Hubs have no climate sensors of their own — pick a pot node (or All nodes) to review soil, temperature, and humidity. Motor commands on the hub come later."
            : "Review temperature, humidity, and soil moisture over time — plus every motor ON/OFF with who started it (user, schedule, or smart rule). Sensor snapshots are hourly (UTC storage, shown in your local timezone)."
        }
      />

      <PageSection variant="content">
        <div className="mx-auto max-w-6xl grid gap-4 sm:gap-6">
          <Card className="shadow-soft">
            <CardHeader className="space-y-3 p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl">Device history</CardTitle>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
                <ThemedSelect
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  className={isHub ? "" : "sm:col-span-1 lg:min-w-[12rem]"}
                >
                  <option value="">Select Device</option>
                  {selectableDevices.map(([id, d]) => (
                    <option key={id} value={id}>
                      {d?.name || `Device ${id}`}
                      {inferDeviceType(id, d?.deviceType) === "hub" ? " (Hub)" : ""}
                    </option>
                  ))}
                </ThemedSelect>
                {isHub ? (
                  <ThemedSelect
                    value={selectedNode}
                    onChange={(e) => setSelectedNode(e.target.value)}
                    disabled={!linkedNodes.length}
                    className="lg:min-w-[12rem]"
                  >
                    <option value="all">All pot nodes</option>
                    {linkedNodes.map((n) => (
                      <option key={n.nodeId} value={n.nodeId}>
                        {n.name || n.nodeId}
                        {n.potIndex ? ` · Pot ${n.potIndex}` : ""}
                      </option>
                    ))}
                  </ThemedSelect>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportCsv}
                  className="h-10 w-full sm:w-auto lg:ml-auto"
                >
                  <Download className="h-3.5 w-3.5" />
                  Report (CSV)
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-2 p-4 pt-0 sm:gap-4 sm:p-6 sm:pt-0 md:grid-cols-3">
              <div className="rounded-xl border border-border bg-cream/50 p-2.5 sm:rounded-2xl sm:p-4">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground">Device</div>
                <div className="mt-1 font-display text-sm sm:mt-2 sm:text-2xl truncate">
                  {selectedDevice ? userDevices[selectedDevice]?.name || selectedDevice : "—"}
                </div>
                <div className="mt-0.5 text-[10px] sm:mt-1 sm:text-xs text-muted-foreground line-clamp-2">
                  {selectedDeviceType ? deviceTypeLabel(selectedDeviceType) : "—"}
                  {" · "}
                  Sensors {filteredRows.length}
                  {!isHub || selectedNode === "all" ? ` · ON ${motorOnCount}` : ""}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-cream/50 p-2.5 sm:rounded-2xl sm:p-4">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground">
                  {isHub ? "Trend" : "Temp trend"}
                </div>
                <div className="mt-1 flex items-center gap-1 sm:mt-2 sm:gap-2">
                  {trend?.t === "up" ? (
                    <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-success" />
                  ) : trend?.t === "down" ? (
                    <TrendingDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-destructive" />
                  ) : null}
                  <span className="font-medium text-xs sm:text-base text-foreground">{trend ? trend.t : "—"}</span>
                </div>
                <div className="mt-0.5 text-[10px] sm:mt-1 sm:text-xs text-muted-foreground hidden sm:block">
                  {isHub ? "Based on last 2 pot hourly points" : "Based on last 2 hourly points"}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-cream/50 p-2.5 sm:rounded-2xl sm:p-4">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground">Motor</div>
                <div className="mt-1 font-display text-sm sm:mt-2 sm:text-2xl">
                  {isHub && selectedNode !== "all" ? "—" : pumpRuns.length}
                </div>
                <div className="mt-0.5 text-[10px] sm:mt-1 sm:text-xs text-muted-foreground line-clamp-2">
                  {isHub && selectedNode !== "all"
                    ? "All nodes for motor"
                    : `${pumpWarnings.length} warn`}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-1.5 sm:flex sm:flex-wrap sm:gap-2">
            {viewTabs.map((tab) => (
              <Button
                key={tab.id}
                size="sm"
                className="h-9 px-2 text-xs sm:h-11 sm:px-6 sm:text-sm"
                variant={view === tab.id ? "default" : "outline"}
                onClick={() => setView(tab.id)}
              >
                <span className="sm:hidden">{tab.short}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </Button>
            ))}
          </div>

          <Card className="shadow-soft overflow-hidden">
            <CardContent className="p-3 sm:p-4 space-y-3">
              <div>
                <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                  {view === "sensors" ? "Sensor" : "Trigger"}
                </div>
                {view === "sensors" ? (
                  <ThemedSelect value={sensorFilter} onChange={(e) => setSensorFilter(e.target.value)}>
                    <option value="all">All sensors</option>
                    <option value="temperature">Temperature</option>
                    <option value="humidity">Humidity</option>
                    <option value="soil">Soil moisture</option>
                  </ThemedSelect>
                ) : (
                  <ThemedSelect value={triggerFilter} onChange={(e) => setTriggerFilter(e.target.value)}>
                    <option value="all">All triggers</option>
                    <option value="user">User manual</option>
                    <option value="schedule">Schedule</option>
                    <option value="smart_irrigation">Smart irrigation</option>
                    <option value="smart_rule">Smart rule</option>
                  </ThemedSelect>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                    From
                  </div>
                  <ThemedDate value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div>
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                    To
                  </div>
                  <ThemedDate value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
              </div>

              <Button variant="outline" size="sm" onClick={resetFilters} className="h-9 w-full sm:w-auto">
                Clear filters
              </Button>
            </CardContent>
          </Card>

          {view === "activity" ? (
            <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cream/60">
                    <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Time</th>
                      {isHub ? <th className="px-5 py-4">Node</th> : null}
                      <th className="px-5 py-4">Event</th>
                      <th className="px-5 py-4">Who / Trigger</th>
                      <th className="px-5 py-4">Duration</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityPaged.items.map((row) => (
                      <tr key={row.id} className="border-t border-border/70 hover:bg-cream/30 transition-colors align-top">
                        <td className="px-5 py-4 whitespace-nowrap">{getFormattedDate(row.at)}</td>
                        <td className="px-5 py-4 whitespace-nowrap">{getFormattedTime(row.at)}</td>
                        {isHub ? (
                          <td className="px-5 py-4 text-xs text-muted-foreground whitespace-nowrap">
                            {row.nodeLabel || "—"}
                          </td>
                        ) : null}
                        <td className="px-5 py-4">
                          <EventPill kind={row.kind} label={row.eventLabel} />
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-xs ${triggerBadgeClass(row.triggerKind)}`}
                          >
                            {row.trigger}
                          </span>
                        </td>
                        <td className="px-5 py-4">{formatDuration(row.duration)}</td>
                        <td className="px-5 py-4">{row.status}</td>
                        <td className="px-5 py-4 text-xs text-muted-foreground max-w-xs">{row.detail}</td>
                      </tr>
                    ))}
                    {!selectedDevice && (
                      <tr>
                        <td className="px-5 py-10 text-center text-muted-foreground" colSpan={isHub ? 8 : 7}>
                          Select a device to view its history.
                        </td>
                      </tr>
                    )}
                    {selectedDevice && !activityRows.length && (
                      <tr>
                        <td className="px-5 py-10 text-center text-muted-foreground" colSpan={isHub ? 8 : 7}>
                          {activityEmptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <HistoryPager pageInfo={activityPaged} onPage={setActivityPage} unitLabel="events" />
            </div>
          ) : null}

          {view === "sensors" ? (
            <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cream/60">
                    <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Time</th>
                      {isHub ? <th className="px-5 py-4">Node</th> : null}
                      <th className="px-5 py-4">Temp (°C)</th>
                      <th className="px-5 py-4">Humidity (%)</th>
                      <th className="px-5 py-4">Soil (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sensorPaged.items.map((row) => (
                      <tr key={row.timestamp} className="border-t border-border/70 hover:bg-cream/30 transition-colors">
                        <td className="px-5 py-4">{getFormattedDate(row.data?.savedAt)}</td>
                        <td className="px-5 py-4">{getFormattedTime(row.data?.savedAt)}</td>
                        {isHub ? (
                          <td className="px-5 py-4">
                            <div className="font-medium">{row.nodeName || row.nodeId || "—"}</div>
                            {row.potIndex ? (
                              <div className="text-xs text-muted-foreground">Pot {row.potIndex}</div>
                            ) : null}
                          </td>
                        ) : null}
                        <td className="px-5 py-4">{row.data?.Temperature ?? "N/A"}</td>
                        <td className="px-5 py-4">{row.data?.Humidity ?? "N/A"}</td>
                        <td className="px-5 py-4">{row.data?.SoilMoisture ?? "N/A"}</td>
                      </tr>
                    ))}
                    {!selectedDevice && (
                      <tr>
                        <td className="px-5 py-10 text-center text-muted-foreground" colSpan={isHub ? 6 : 5}>
                          Select a device to view its history.
                        </td>
                      </tr>
                    )}
                    {selectedDevice && !filteredRows.length && (
                      <tr>
                        <td className="px-5 py-10 text-center text-muted-foreground" colSpan={isHub ? 6 : 5}>
                          {sensorEmptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <HistoryPager pageInfo={sensorPaged} onPage={setSensorPage} unitLabel="hours" />
            </div>
          ) : null}

          {view === "pump" ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
                <div className="px-5 py-4 text-xs uppercase tracking-widest text-muted-foreground">Motor runs</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-cream/60">
                      <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="px-5 py-4">ON at</th>
                        <th className="px-5 py-4">OFF at</th>
                        <th className="px-5 py-4">Ran</th>
                        <th className="px-5 py-4">Trigger</th>
                        <th className="px-5 py-4">Status</th>
                        <th className="px-5 py-4">At start</th>
                        <th className="px-5 py-4">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pumpRunPaged.items.map((r) => (
                        <tr key={r.id} className="border-t border-border/70 hover:bg-cream/30 transition-colors align-top">
                          <td className="px-5 py-4">
                            <div>{getFormattedDate(r.startedAt || r.queuedAt)}</div>
                            <div className="text-xs text-muted-foreground">
                              {getFormattedTime(r.startedAt || r.queuedAt)}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            {r.endedAt ? (
                              <>
                                <div>{getFormattedDate(r.endedAt)}</div>
                                <div className="text-xs text-muted-foreground">{getFormattedTime(r.endedAt)}</div>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-5 py-4">
                            {r.status === "running" ? (
                              <>
                                <div className="font-display text-lg tabular-nums text-success">
                                  {formatCountdown(pumpRunRemainingSec(r))}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  planned {formatDuration(r.plannedDurationSec)}
                                </div>
                              </>
                            ) : (
                              <>
                                <div>{formatDuration(r.actualDurationSec)}</div>
                                <div className="text-xs text-muted-foreground">
                                  planned {formatDuration(r.plannedDurationSec)}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex rounded-full border px-2.5 py-1 text-xs ${triggerBadgeClass(r.triggerKind)}`}
                            >
                              {r.sourceLabel || r.source}
                            </span>
                          </td>
                          <td className="px-5 py-4">{statusLabel(r.status)}</td>
                          <td className="px-5 py-4 text-xs text-muted-foreground">
                            {(() => {
                              const snap = sanitizeSensorSnapshot(r);
                              return `Soil ${formatSensorValue(snap.soilAtStart)}% · ${formatSensorValue(snap.tempAtStart)}°C · ${formatSensorValue(snap.humidityAtStart)}%`;
                            })()}
                          </td>
                          <td className="px-5 py-4 text-xs">
                            {pumpRunNote(r)}
                          </td>
                        </tr>
                      ))}
                      {!selectedDevice && (
                        <tr>
                          <td className="px-5 py-10 text-center text-muted-foreground" colSpan={7}>
                            Select a device to view motor history.
                          </td>
                        </tr>
                      )}
                      {selectedDevice && !pumpRuns.length && (
                        <tr>
                          <td className="px-5 py-10 text-center text-muted-foreground" colSpan={7}>
                            {pumpEmptyMessage}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <HistoryPager pageInfo={pumpRunPaged} onPage={setPumpRunPage} unitLabel="logs" />
              </div>

              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
                <div className="px-5 py-4 text-xs uppercase tracking-widest text-muted-foreground">
                  Motor warnings
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-cream/60">
                      <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="px-5 py-4">When</th>
                        <th className="px-5 py-4">Level</th>
                        <th className="px-5 py-4">Source</th>
                        <th className="px-5 py-4">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {warningPaged.items.map((w) => (
                        <tr key={w.id} className="border-t border-border/70 hover:bg-cream/30 transition-colors">
                          <td className="px-5 py-4">
                            <div>{getFormattedDate(w.at)}</div>
                            <div className="text-xs text-muted-foreground">{getFormattedTime(w.at)}</div>
                          </td>
                          <td className="px-5 py-4 capitalize">{w.level}</td>
                          <td className="px-5 py-4">{w.source || "—"}</td>
                          <td className="px-5 py-4">{w.message}</td>
                        </tr>
                      ))}
                      {selectedDevice && !pumpWarnings.length && (
                        <tr>
                          <td className="px-5 py-10 text-center text-muted-foreground" colSpan={4}>
                            No motor warnings in this range. Warnings appear only for pump failures — timeout, wiring, manual switch, or device offline.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <HistoryPager pageInfo={warningPaged} onPage={setWarningPage} unitLabel="logs" />
              </div>

              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
                <div className="px-5 py-4 text-xs uppercase tracking-widest text-muted-foreground">
                  Motor event log
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-cream/60">
                      <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="px-5 py-4">When</th>
                        <th className="px-5 py-4">Level</th>
                        <th className="px-5 py-4">Source</th>
                        <th className="px-5 py-4">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logPaged.items.map((l) => (
                        <tr key={l.id} className="border-t border-border/70 hover:bg-cream/30 transition-colors">
                          <td className="px-5 py-4">
                            <div>{getFormattedDate(l.at)}</div>
                            <div className="text-xs text-muted-foreground">{getFormattedTime(l.at)}</div>
                          </td>
                          <td className="px-5 py-4 capitalize">{l.level}</td>
                          <td className="px-5 py-4">{l.source || "—"}</td>
                          <td className="px-5 py-4">{l.message}</td>
                        </tr>
                      ))}
                      {selectedDevice && !pumpLogs.length && (
                        <tr>
                          <td className="px-5 py-10 text-center text-muted-foreground" colSpan={4}>
                            No motor event logs yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <HistoryPager pageInfo={logPaged} onPage={setLogPage} unitLabel="logs" />
              </div>
            </>
          ) : null}
        </div>
      </PageSection>
    </PageShell>
  );
}
